/**
 * Front Door CDN icons — `https://cdn.valut.services/assets/icons/{name}.svg`.
 * Prefer API `icon_url` when already on Front Door; remap legacy BaaS `/cdn/icons`
 * (incl. foxbit) and FoxBit statics to Front Door. Never leave bare `cdn/icons/...`.
 */

const STATEMENT_RAIL_ICON_KEYS = {
  PIX: "pix",
  TED: "ted",
  SWIFT: "swift",
  ACH: "ach",
  WIRE: "wire",
  SEPA: "sepa",
  BOLETO: "boleto",
  BILL: "boleto",
  CONTAS: "boleto",
  TRANSFER: "extrato",
  TRANSFERENCIA: "extrato",
  TRANSFERÊNCIA: "extrato",
  INTERNAL: "extrato",
  INTERNA: "extrato",
};

/** Stablecoin symbols used in statement descriptions / category names. */
const STABLE_ASSET_STEMS = ["usdc", "usdt", "eurc", "brl1"];

/** Prod Front Door icons base (no trailing slash). Matches BaaS Stables:IconCdnBaseUrl. */
export const DEFAULT_CDN_ICONS_BASE = "https://cdn.valut.services/assets/icons";

const FOXBIT_STATICS_HOST = "statics.foxbit.com.br";

/** Legacy statement Pix SVG is white-only (no teal) — looks washed gray on light circles. */
const LEGACY_GRAY_PIX_BLOB =
  "https://valutcdnstatic.blob.core.windows.net/statement/pix.svg";

/**
 * Front Door / BaaS local `pix.svg` is a teal circle with a white "G" glyph — not the Pix diamond.
 * Prefer static.valut.app brand icon for all statement / Área Pix rows.
 */
const WRONG_FRONT_DOOR_PIX_ICON =
  "https://cdn.valut.services/assets/icons/pix.svg";

/** Legacy balance-icons Pix (filename has a space) → static CDN brand icon. */
const LEGACY_PIX_BALANCE_ICON_BLOB =
  "https://valutcdnstatic.blob.core.windows.net/balance-icons/pagamento qrcode.svg";
const LEGACY_PIX_BALANCE_ICON_BLOB_ENCODED =
  "https://valutcdnstatic.blob.core.windows.net/balance-icons/pagamento%20qrcode.svg";
const PIX_BALANCE_ICON_URL = "https://static.valut.app/balance-icons/pix.svg";
const COMPRA_STABLE_BALANCE_ICON_URL =
  "https://static.valut.app/balance-icons/compra-stable.svg";
const VENDA_STABLE_BALANCE_ICON_URL =
  "https://static.valut.app/balance-icons/venda-stable.svg";

const normalizeBaseUrl = (value) =>
  String(value || "")
    .trim()
    .replace(/\/+$/, "");

const isAbsoluteHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

/**
 * Resolve Front Door CDN icons base (`REACT_APP_CDN_ICONS_BASE` or default).
 * Pass `""` to disable Front Door and use local `/cdn/icons` fallback.
 * @param {string | null | undefined} [override]
 */
export const resolveCdnIconsBaseUrl = (override) => {
  if (override !== undefined && override !== null) {
    return normalizeBaseUrl(override);
  }
  const fromEnv =
    (typeof process !== "undefined" && process.env?.REACT_APP_CDN_ICONS_BASE?.trim()) || "";
  return normalizeBaseUrl(fromEnv) || DEFAULT_CDN_ICONS_BASE;
};

/**
 * Normalize a CDN path so it never stays as bare `cdn/icons/...` (browser-relative to carteira).
 * @param {string} raw
 * @returns {string} path starting with `/`
 */
export const normalizeBaasCdnIconPath = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return "";
  if (isAbsoluteHttpUrl(trimmed)) {
    try {
      return new URL(trimmed).pathname || "";
    } catch {
      return "";
    }
  }
  const withoutOrigin = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, "");
  const path = withoutOrigin.startsWith("/") ? withoutOrigin : `/${withoutOrigin}`;
  return path.replace(/^\/+/, "/");
};

/**
 * Extract icon stem from legacy BaaS paths:
 * `/cdn/icons/{symbol}.svg`, `/cdn/icons/foxbit/{symbol}.svg`, `/cdn/icons/proxy?...`
 * @param {string} raw
 * @returns {string | null}
 */
const extractLegacyCdnIconStem = (raw) => {
  const path = normalizeBaasCdnIconPath(raw);
  if (!path) return null;

  const foxbit = path.match(/^\/cdn\/icons\/foxbit\/([^/?#]+?)(?:\.svg)?$/i);
  if (foxbit?.[1]) {
    return String(foxbit[1]).toLowerCase().replace(/\.svg$/i, "");
  }

  const simple = path.match(/^\/cdn\/icons\/([^/?#]+?)(?:\.svg)?$/i);
  if (simple?.[1]) {
    const stem = String(simple[1]).toLowerCase().replace(/\.svg$/i, "");
    if (stem === "proxy" || stem === "foxbit") return null;
    return stem;
  }

  return null;
};

/**
 * Stem from FoxBit colored statics URL, if any.
 * @param {string} raw
 * @returns {string | null}
 */
const extractFoxBitStaticsStem = (raw) => {
  if (!isAbsoluteHttpUrl(raw)) return null;
  try {
    const url = new URL(String(raw).trim());
    if (url.hostname.toLowerCase() !== FOXBIT_STATICS_HOST) return null;
    const file = url.pathname.split("/").filter(Boolean).pop() || "";
    const stem = file.replace(/\.svg$/i, "").trim().toLowerCase();
    return stem || null;
  } catch {
    return null;
  }
};

/**
 * True when URL is already under Front Door assets/icons (or configured CDN base).
 * @param {string} raw
 * @param {string} cdnBase
 */
const isFrontDoorIconUrl = (raw, cdnBase) => {
  if (!isAbsoluteHttpUrl(raw) || !cdnBase) return false;
  const normalized = String(raw).trim().replace(/\/+$/, "");
  return (
    normalized === cdnBase ||
    normalized.startsWith(`${cdnBase}/`) ||
    /^https:\/\/cdn\.valut\.services\/assets\/icons(\/|$)/i.test(normalized)
  );
};

/**
 * True when URL is the legacy white-only Pix diamond (no brand teal).
 * @param {string} raw
 */
export const isLegacyGrayPixIconUrl = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === "pix.svg" || lower === "/statement/pix.svg" || lower === "statement/pix.svg") {
    return true;
  }
  return lower === LEGACY_GRAY_PIX_BLOB || /\/statement\/pix\.svg$/i.test(trimmed);
};

/**
 * True when URL is Front Door / relative BaaS `pix.svg` (teal "G" glyph, not Pix diamond).
 * @param {string} raw
 */
export const isWrongFrontDoorPixIconUrl = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === WRONG_FRONT_DOOR_PIX_ICON.toLowerCase()) return true;
  if (/^https:\/\/cdn\.valut\.services\/assets\/icons\/pix\.svg$/i.test(trimmed)) return true;
  const path = normalizeBaasCdnIconPath(trimmed);
  return /^\/cdn\/icons\/pix(?:\.svg)?$/i.test(path);
};

/**
 * True when URL is the legacy blob balance-icons Pix ("pagamento qrcode.svg").
 * @param {string} raw
 */
export const isLegacyPixBalanceIconUrl = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (
    lower === LEGACY_PIX_BALANCE_ICON_BLOB.toLowerCase() ||
    lower === LEGACY_PIX_BALANCE_ICON_BLOB_ENCODED.toLowerCase()
  ) {
    return true;
  }
  return (
    /valutcdnstatic\.blob\.core\.windows\.net/i.test(trimmed) &&
    /\/balance-icons\/pagamento(?:%20| )qrcode\.svg$/i.test(trimmed)
  );
};

/**
 * True when URL is any known Pix statement asset that should use the brand diamond
 * (Front Door teal-G, legacy white/gray blob, balance-icons pagamento, or bare pix.svg).
 * @param {string} raw
 */
export const shouldRemapToPixBalanceIcon = (raw) => {
  if (
    isWrongFrontDoorPixIconUrl(raw) ||
    isLegacyGrayPixIconUrl(raw) ||
    isLegacyPixBalanceIconUrl(raw)
  ) {
    return true;
  }
  const trimmed = String(raw || "").trim();
  if (!trimmed) return false;
  // Catch query/hash variants and alternate hosts serving the wrong pix.svg asset.
  if (/\/(?:assets\/icons|cdn\/icons|statement)\/pix\.svg(?:[?#]|$)/i.test(trimmed)) {
    return true;
  }
  return false;
};

/**
 * Category / rail looks like Pix (incl. estorno / devolução) — always brand diamond.
 * @param {{ category?: { name?: string, Name?: string, icon?: string }, rail?: string, type?: string, is_refund?: boolean, isRefund?: boolean }} item
 */
export const isPixStatementItem = (item) => {
  const rail = String(item?.rail || item?.type || "")
    .trim()
    .toUpperCase();
  if (rail === "PIX") return true;
  if (item?.is_refund || item?.isRefund) return true;
  const categoryName = String(item?.category?.name || item?.category?.Name || "").toUpperCase();
  return (
    categoryName.includes("PIX") ||
    categoryName.includes("ESTORNO") ||
    categoryName.includes("DEVOLU")
  );
};

/**
 * Prefer API `icon_url`, then symbol → Front Door `{cdn}/{symbol}.svg`.
 * Absolute Front Door URLs are used as-is (except wrong Front Door `pix.svg` → static).
 * Legacy `/cdn/icons/...` (relative or absolute on any host) and FoxBit statics map to Front Door.
 * All wrong/legacy Pix statement icons remap to static.valut.app Pix diamond.
 *
 * @param {string | null | undefined} iconUrl from API (`icon_url`)
 * @param {{ symbol?: string, baasBaseUrl?: string, cdnIconsBaseUrl?: string }} [options]
 * @returns {string | null}
 */
export const resolveBaasIconUrl = (iconUrl, options = {}) => {
  const { symbol, baasBaseUrl, cdnIconsBaseUrl } = options;
  const cdnBase = resolveCdnIconsBaseUrl(cdnIconsBaseUrl);
  const baasBase = normalizeBaseUrl(baasBaseUrl);
  const raw = String(iconUrl || "").trim();

  if (raw) {
    if (shouldRemapToPixBalanceIcon(raw)) {
      return PIX_BALANCE_ICON_URL;
    }

    if (isFrontDoorIconUrl(raw, cdnBase)) return raw;

    const foxbitStem = extractFoxBitStaticsStem(raw);
    if (foxbitStem) return buildBaasCdnIconUrl(foxbitStem, { cdnIconsBaseUrl: cdnBase });

    const legacyStem = extractLegacyCdnIconStem(raw);
    if (legacyStem) return buildBaasCdnIconUrl(legacyStem, { cdnIconsBaseUrl: cdnBase });

    // Absolute non-legacy URL (e.g. partner CDN) — keep as-is.
    if (isAbsoluteHttpUrl(raw)) return raw;

    const path = normalizeBaasCdnIconPath(raw);
    if (!path) return null;
    // Non-cdn relative paths: prefer Front Door host only when baas base missing.
    if (baasBase) return `${baasBase}${path}`;
    // Never return bare relative paths that look like CDN (would load from carteira origin).
    if (/^\/cdn\//i.test(path)) {
      return buildBaasCdnIconUrl(symbol, { cdnIconsBaseUrl: cdnBase }) || null;
    }
    return path;
  }

  const key = String(symbol || "")
    .trim()
    .toLowerCase()
    .replace(/\.svg$/i, "");
  if (!key) return null;
  return buildBaasCdnIconUrl(key, { cdnIconsBaseUrl: cdnBase });
};

/**
 * Front Door CDN icon URL for stables / Extrato assets.
 * @param {string} name icon stem (e.g. usdc, pix)
 * @param {string | { baasBaseUrl?: string, cdnIconsBaseUrl?: string }} [baseOrOptions]
 *        string = legacy BaaS origin (ignored for primary Front Door build);
 *        options.cdnIconsBaseUrl overrides env/default.
 */
export const buildBaasCdnIconUrl = (name, baseOrOptions) => {
  let key = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\.svg$/i, "");
  if (!key) return "";

  // EURC reuses USDC coin art until a dedicated CDN asset exists.
  if (key === "eurc") key = "usdc";

  // Front Door / local BaaS `pix.svg` is the wrong teal "G" glyph — use brand diamond.
  if (key === "pix") return PIX_BALANCE_ICON_URL;

  const options =
    baseOrOptions && typeof baseOrOptions === "object" && !Array.isArray(baseOrOptions)
      ? baseOrOptions
      : { baasBaseUrl: baseOrOptions };

  const cdnBase =
    options.cdnIconsBaseUrl !== undefined
      ? resolveCdnIconsBaseUrl(options.cdnIconsBaseUrl)
      : resolveCdnIconsBaseUrl();
  if (cdnBase) {
    return `${cdnBase}/${encodeURIComponent(key)}.svg`;
  }

  // Optional: local BaaS `/cdn/icons` when Front Door base is explicitly cleared.
  const path = `/cdn/icons/${encodeURIComponent(key)}.svg`;
  const baasBase = normalizeBaseUrl(options.baasBaseUrl);
  return baasBase ? `${baasBase}${path}` : path;
};

/**
 * Build a searchable haystack from statement / Baas transaction fields.
 * @param {{ category?: { name?: string, Name?: string }, description?: string, name?: string, counterpartyName?: string, rail?: string, type?: string }} item
 */
const statementIconHaystack = (item) =>
  [
    item?.description,
    item?.name,
    item?.counterpartyName,
    item?.category?.name,
    item?.category?.Name,
    item?.rail,
    item?.type,
  ]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();

/**
 * Stablecoin asset for carteira-style CurrencyIcon (compra/venda/transfer stables).
 * Prefer an explicit USDC/USDT/EURC/BRL1 mention; otherwise default USDC for "stables" rows.
 * @param {{ category?: { name?: string, Name?: string }, description?: string, name?: string, counterpartyName?: string, rail?: string, type?: string }} item
 * @returns {string | null} lowercase asset key, or null when not a stables ledger row
 */
export const resolveBaasTransactionStableAsset = (item) => {
  const haystack = statementIconHaystack(item);
  if (!haystack) return null;

  for (const stem of STABLE_ASSET_STEMS) {
    if (new RegExp(`\\b${stem}\\b`, "i").test(haystack)) return stem;
  }

  // e.g. "TRANSFERÊNCIA - Compra stables / INTERNA", "Venda stables"
  if (/\bSTABLES?\b/.test(haystack) || /\b(COMPRA|VENDA)\s+STABLE/.test(haystack)) {
    return "usdc";
  }

  return null;
};

/**
 * Valut-padrão category icon for compra/venda stables statement rows (not brand USDC coin).
 * @param {{ category?: { name?: string, Name?: string }, description?: string, name?: string, counterpartyName?: string, rail?: string, type?: string }} item
 * @returns {string | null}
 */
export const resolveStablesStatementCategoryIcon = (item) => {
  const haystack = statementIconHaystack(item);
  if (!haystack) return null;
  if (!/\bSTABLES?\b/.test(haystack) && !/\b(COMPRA|VENDA)\s+STABLE/.test(haystack)) {
    return null;
  }
  if (/\bVENDA\b/.test(haystack)) return VENDA_STABLE_BALANCE_ICON_URL;
  if (/\bCOMPRA\b/.test(haystack)) return COMPRA_STABLE_BALANCE_ICON_URL;
  return COMPRA_STABLE_BALANCE_ICON_URL;
};

/**
 * True when URL is the brand-blue USDC coin (FoxBit / Front Door).
 * Used to remap statement category icons to compra/venda-stable — not for color tinting.
 * @param {string | null | undefined} url
 */
export const isBrandUsdcIconUrl = (url) =>
  /\/usdc\.svg(?:[?#]|$)/i.test(String(url || "").trim());

/**
 * @deprecated Do not apply to brand USDC coin SVGs.
 * `brightness(0)` collapses concentric circles / $ into a solid mustard circle on light cards.
 * Kept only for any residual callers; CurrencyIcon and stables UIs use CDN colors as-is.
 */
export const BRAND_USDC_GOLD_FILTER =
  "brightness(0) saturate(100%) invert(72%) sepia(48%) saturate(650%) hue-rotate(352deg) brightness(96%) contrast(90%)";

/** @deprecated Alias of BRAND_USDC_GOLD_FILTER. */
export const BRAND_ICON_GOLD_FILTER = BRAND_USDC_GOLD_FILTER;

/**
 * Extrato / statement list UIs: never force gold — icon color comes from the bank CDN link.
 * @param {string | null | undefined} _url
 */
export const shouldTintExtratoIconGold = (_url) => false;

/**
 * Resolve Extrato row icon: prefer API category.icon, else rail → Front Door CDN / legacy blob.
 * Pix / estorno / devolução always use the static.valut.app brand diamond (never teal-G).
 * Stables compra/venda map to Valut balance-icons (not brand USDC/USDT coins).
 * @param {{ category?: { icon?: string, name?: string }, rail?: string, type?: string, is_refund?: boolean, description?: string }} item
 * @param {string} [baasBaseUrl]
 */
export const resolveExtratoItemIcon = (item, baasBaseUrl) => {
  // Pix / estorno / devolução — always brand gold diamond (never teal-G or legacy gray).
  if (isPixStatementItem(item)) {
    return PIX_BALANCE_ICON_URL;
  }

  const fromApi = item?.category?.icon || item?.category?.Icon;
  if (fromApi) {
    const resolved = resolveBaasIconUrl(fromApi, { baasBaseUrl });
    if (resolved) {
      // Category still pointing at brand USDC → prefer Valut compra/venda-stable when applicable.
      if (isBrandUsdcIconUrl(resolved)) {
        const stablesIcon = resolveStablesStatementCategoryIcon(item);
        if (stablesIcon) return stablesIcon;
      }
      return resolved;
    }
  }

  const stablesIcon = resolveStablesStatementCategoryIcon(item);
  if (stablesIcon) return stablesIcon;

  const stableAsset = resolveBaasTransactionStableAsset(item);
  if (stableAsset) {
    // Named asset (USDT etc.) still uses CDN coin; USDC tinted gold in console list renderers.
    return buildBaasCdnIconUrl(stableAsset, { baasBaseUrl });
  }

  const rail = String(item?.rail || item?.type || "")
    .trim()
    .toUpperCase();
  if (rail && rail !== "—" && STATEMENT_RAIL_ICON_KEYS[rail]) {
    return buildBaasCdnIconUrl(STATEMENT_RAIL_ICON_KEYS[rail], { baasBaseUrl });
  }

  // Internal / unnamed transfers without an explicit rail.
  const haystack = statementIconHaystack(item);
  if (/\bTRANSFER|\bINTERNA\b|\bINTERNAL\b/.test(haystack)) {
    return buildBaasCdnIconUrl("extrato", { baasBaseUrl });
  }

  const key = STATEMENT_RAIL_ICON_KEYS[rail] || "extrato";
  return buildBaasCdnIconUrl(key, { baasBaseUrl });
};

/**
 * Resolve a statement/list category icon URL (Pix Área, Extrato, ReceiptList).
 * Remaps wrong Front Door teal-"G" pix.svg, legacy white blob, and balance-icons Pix
 * to the static.valut.app Pix diamond.
 * @param {string | null | undefined} iconUrl
 * @returns {string | null}
 */
export const resolveStatementCategoryIcon = (iconUrl) => {
  const raw = String(iconUrl || "").trim();
  if (!raw) return null;
  return resolveBaasIconUrl(raw, { symbol: "pix" }) || raw;
};
